import IconTextButton from '@/common/atoms/IconTextButton'
import NextStepItem from '@/nextStep/components/NextStepItem'
import MeetingThreadGovernanceChanges from './MeetingThreadGovernanceChanges'
import { ThreadApprovedProposal } from '@rolebase/shared/model/meetingProposals'
import { EditorHandle } from '@/editor'
import CollabEditor from '@/editor/components/CollabEditor'
import { useOrgContext } from '@/org/contexts/OrgContext'
import ThreadStatusIcon from '@/thread/components/ThreadStatusIcon'
import useSaveThreadMeetingNote from '@/thread/hooks/useSaveThreadMeetingNote'
import { CloseIcon } from '@chakra-ui/icons'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Text,
} from '@chakra-ui/react'
import {
  NextStepFragment,
  ThreadActivityFragment,
  Thread_Status_Enum,
  ThreadsWithMeetingNoteSubscription,
  Visibility_Enum,
} from '@gql'
import { Draggable } from '@hello-pangea/dnd'
import { ThreadActivityDataMeetingNote } from '@rolebase/shared/model/thread_activity'
import { getVisibilityAccess } from '@rolebase/shared/model/visibility'
import { ThreadWeather } from '@rolebase/shared/model/threadWeather'
import React, { useCallback, useContext, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckIcon } from 'src/icons'
import ThreadItem from '../../thread/components/ThreadItem'
import ThreadWeatherBadge from './ThreadWeatherBadge'
import { MeetingContext } from '../contexts/MeetingContext'
import {
  MeetingThreadsContext,
  NotAllowedThread,
} from './MeetingThreadsDragDropContext'

// A third smaller than the 40 first tried (Yohan, 17/09, #106): still read
// first, without weighing on the line.
const WEATHER_ICON_SIZE = 27

// Wide enough for the badge and a gap: ThreadItem bleeds 8px to the left
// (`mx={-2}`) for its hover background. Unchanged by the badge's size: it also
// sets the left padding of the notes and the next steps below, so narrowing it
// would shift the whole tension left.
const WEATHER_COLUMN_WIDTH = 14

export type CircleThreadWithMeetingNote = NonNullable<
  ThreadsWithMeetingNoteSubscription['thread']
>[0]

interface Props {
  thread: CircleThreadWithMeetingNote | NotAllowedThread
  index: number
  // The tension's own next steps, still owed (G2-D25/D26). Read-only here: one
  // has just written them, they are not done, and reopening a tension goes
  // through the tension itself, not through its steps.
  nextSteps?: NextStepFragment[]
  // What the tension produced once its proposal was approved (REU-13). Only a
  // Governance step passes them; a governance tension carries no next step, so
  // the two never show together.
  approvedProposals?: ThreadApprovedProposal[]
  onRemove?(): void
  // The step this line belongs to, and what it says about this tension (#89).
  // `showWeather` is false in the free order, the only view that shows no
  // weather at all (REU89-D9).
  stepId?: string
  weather?: ThreadWeather | null
  showWeather?: boolean
  // Settled as the list reads it: a reopening shows before the server's echo,
  // so the rainbow and the group of the line agree (#106).
  closed?: boolean
  onReopen?(): void
  // Closes the tension in one click, from its line (Yohan, 21/09, #108).
  // Undone by the weather badge, which reopens a settled tension.
  onClose?(): void
  // Only the free order writes the agenda (REU89-D9): elsewhere the line shows
  // but does not move.
  dragDisabled?: boolean
  // Set only when this thread sits in a step of the other type AND a step of
  // its own type exists in this meeting. Absent means silence: a mismatch with
  // nowhere to go would only be a reproach.
  moveTarget?: {
    title: string
    onMove(): void
  }
}

export default function MeetingStepContentThreadItem({
  thread,
  index,
  nextSteps,
  approvedProposals,
  onRemove,
  moveTarget,
  stepId,
  weather,
  showWeather,
  closed,
  onReopen,
  onClose,
  dragDisabled,
}: Props) {
  const { t } = useTranslation()
  const editorRef = useRef<EditorHandle>(null)
  const meetingState = useContext(MeetingContext)!
  const { meeting, editable, participants } = meetingState

  const { setThreadWeather } = useContext(MeetingThreadsContext)!

  const saveThreadMeetingNote = useSaveThreadMeetingNote(thread.id, meeting?.id)

  // Privacy
  const isAllowed = !('notAllowed' in thread)

  // Activity data
  const activity = isAllowed
    ? (thread.activities[0] as ThreadActivityFragment | undefined)
    : undefined
  const activityData = activity?.data as
    | ThreadActivityDataMeetingNote
    | undefined
  const hasNotes = !!activityData?.notes

  // Meeting participants who cannot read this tension at its level (CONF-02).
  // Display only, the Hasura permission decides.
  const { orgData } = useOrgContext()
  const notAllowedParticipants = useMemo(() => {
    if (!orgData || 'notAllowed' in thread) return []
    const namedMemberIds = thread.extra_members.map((em) => em.memberId)
    return participants.filter(
      (p) =>
        !getVisibilityAccess({
          orgData,
          circleId: thread.circleId,
          visibility: thread.visibility,
          memberId: p.member.id,
          namedMemberIds,
          isOrgWriter: false,
        }).canRead
    )
  }, [participants, thread, orgData])

  // Update notes (#197: created once, even before the subscription returns it)
  const handleNotesChange = useCallback(
    (notes: string) => saveThreadMeetingNote(activity?.id, notes),
    [activity?.id, saveThreadMeetingNote]
  )

  if (!meeting) return null

  return (
    <Draggable
      draggableId={thread.id}
      index={index}
      isDragDisabled={dragDisabled}
    >
      {(provided, snapshot) => (
        <Box ref={provided.innerRef} {...provided.draggableProps} pb={10}>
          {isAllowed ? (
            <>
              {/* The weather heads the tension, beside its status icon
                  (Yohan, 17/09, #106): it says what presses, so it is read
                  before the rest. Its own column, centred on the title AND the
                  notes — the next steps below are what the tension produced,
                  not the tension. The badge's size does not change that
                  (Yohan, 18/09): aligned on the title's line it sits too high.
                  Out of ThreadItem on purpose: there, the LinkOverlay covers
                  the card and a click on the badge opened the tension instead
                  of its menu. */}
              <Flex align="center">
                {showWeather && (
                  <Flex w={WEATHER_COLUMN_WIDTH} flexShrink={0}>
                    <ThreadWeatherBadge
                      value={weather}
                      size={WEATHER_ICON_SIZE}
                      done={closed}
                      readOnly={!editable || !stepId}
                      onReopen={onReopen}
                      onChange={(value) =>
                        stepId && setThreadWeather(stepId, thread.id, value)
                      }
                    />
                  </Flex>
                )}

                <Box flex={1} minW={0}>
                  <ThreadItem
                    thread={thread}
                    showIcon
                    showMember
                    isDragging={snapshot.isDragging}
                    mb={2}
                    mx={-2}
                    labelProps={{
                      fontSize: 'md',
                      fontFamily: 'heading',
                      fontWeight: 'medium',
                    }}
                    {...provided.dragHandleProps}
                  >
                    {onClose && !closed && (
                      <IconTextButton
                        aria-label={t('MeetingStepContentThreadItem.close')}
                        size="xs"
                        icon={<CheckIcon />}
                        // A round green vignette, the weight of a decision,
                        // beside the grey one that only takes it off (#108)
                        isRound
                        variant="solid"
                        colorScheme="green"
                        ml={2}
                        zIndex={1}
                        onClick={onClose}
                      />
                    )}
                    {onRemove && (
                      <IconTextButton
                        aria-label={t('common.remove')}
                        size="xs"
                        isRound
                        variant="solid"
                        icon={<CloseIcon />}
                        ml={2}
                        zIndex={1}
                        onClick={onRemove}
                      />
                    )}
                  </ThreadItem>

                  {moveTarget && (
                    <HStack spacing={2} mb={2} fontSize="sm" color="gray.500">
                      <Text>{t('MeetingStepContentThreadItem.misplaced')}</Text>
                      <Button
                        size="xs"
                        variant="link"
                        colorScheme="blue"
                        onClick={moveTarget.onMove}
                      >
                        {t('MeetingStepContentThreadItem.moveToStep', {
                          step: moveTarget.title,
                        })}
                      </Button>
                    </HStack>
                  )}

                  {meeting.visibility === Visibility_Enum.Role &&
                    thread.visibility !== Visibility_Enum.Role && (
                      <Alert status="warning" mb={2}>
                        <AlertIcon />
                        <AlertDescription>
                          {t(
                            'MeetingStepContentThreadItem.widerThanMeetingWarning'
                          )}
                        </AlertDescription>
                      </Alert>
                    )}

                  {notAllowedParticipants.length > 0 && (
                    <Alert status="warning" mb={2}>
                      <AlertIcon />
                      <AlertDescription>
                        {t(
                          'MeetingStepContentThreadItem.notAllowedParticipantsWarning',
                          {
                            count: notAllowedParticipants.length,
                            participants: notAllowedParticipants
                              .map((p) => p.member.name)
                              .join(', '),
                          }
                        )}
                      </AlertDescription>
                    </Alert>
                  )}

                  {(editable || hasNotes) && (
                    <CollabEditor
                      ref={editorRef}
                      docId={`thread-${thread.id}-meeting-${meeting.id}`}
                      value={activityData?.notes || ''}
                      placeholder={t(
                        'ThreadActivityMeetingNote.notesPlaceholder'
                      )}
                      readOnly={!editable}
                      saveEvery={10000}
                      onSave={handleNotesChange}
                    />
                  )}
                </Box>
              </Flex>

              <Box pl={showWeather ? WEATHER_COLUMN_WIDTH : undefined}>
                {nextSteps && nextSteps.length > 0 && (
                  <Box mt={3}>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      textTransform="uppercase"
                      color="gray.500"
                      _dark={{ color: 'gray.400' }}
                      mb={2}
                    >
                      {t('MeetingStepContentThreadItem.nextSteps')}
                    </Text>
                    {nextSteps.map((nextStep) => (
                      // No checkbox, no click (G2-D26): the step was just
                      // written, it is not done, and it gets ticked where it is
                      // reviewed — the Next steps board and the personal page.
                      <NextStepItem
                        key={nextStep.id}
                        nextStep={nextStep}
                        mb={2}
                      />
                    ))}
                  </Box>
                )}

                {approvedProposals && (
                  <MeetingThreadGovernanceChanges
                    proposals={approvedProposals}
                  />
                )}
              </Box>
            </>
          ) : (
            // Same indent as its readable neighbours when they carry a
            // weather column, or it would start further left than them.
            <Flex
              align="center"
              pl={showWeather ? WEATHER_COLUMN_WIDTH : undefined}
            >
              <ThreadStatusIcon value={Thread_Status_Enum.Blocked} readOnly />
              <Text fontStyle="italic" ml={2}>
                {t('MeetingStepContentThreadItem.notAllowed')}
              </Text>
            </Flex>
          )}
        </Box>
      )}
    </Draggable>
  )
}
